import { isHoliday } from "nyse-holidays";
import { getDateRange } from "../../utils/strategyPerformanceUtils";

/** NYSE session calendar (US Eastern) for signal timestamps — not UTC (avoids Sun US → Mon UTC). */
const NYSE_SIGNAL_CALENDAR_TZ = "America/New_York";

function mean(xs) {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function std(xs, ddof = 1) {
  const n = xs.length;
  if (n - ddof <= 0) return NaN;
  const m = mean(xs);
  const v = xs.reduce((acc, x) => acc + (x - m) ** 2, 0) / (n - ddof);
  return Math.sqrt(v);
}

function parseIsoToLocalDate(date) {
  // date: "YYYY-MM-DD" -> local Date at midnight (matches how nyse-holidays expects dates)
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function isTradingDayNY(dateStr) {
  const ymd =
    typeof dateStr === "string" ? dateStr.split("T")[0] : String(dateStr);
  const dt = parseIsoToLocalDate(ymd);
  const dow = dt.getDay(); // 0 Sun ... 6 Sat
  if (dow === 0 || dow === 6) return false;
  return !isHoliday(dt);
}

function navSeriesKeysSorted(strategyNavSeries) {
  const keys =
    strategyNavSeries instanceof Map
      ? Array.from(strategyNavSeries.keys())
      : Object.keys(strategyNavSeries || {});
  return keys
    .map((d) => d.split("T")[0])
    .sort((a, b) => a.localeCompare(b));
}

/**
 * YYYY-MM-DD bounds for Strategy Performance metrics/chart: preset periods via getDateRange,
 * else first/last date in strategyNavSeries (inception).
 */
function resolveNavPeriodBounds(
  strategyNavSeries,
  period,
  customFromDate,
  customToDate,
  appliedCustomFromDate,
  appliedCustomToDate
) {
  if (!strategyNavSeries || typeof strategyNavSeries !== "object") {
    return { startDate: null, endDate: null };
  }
  const navDates = navSeriesKeysSorted(strategyNavSeries);
  if (!navDates.length) return { startDate: null, endDate: null };

  const fromDate =
    period === "Custom" ? appliedCustomFromDate : customFromDate;
  const toDate = period === "Custom" ? appliedCustomToDate : customToDate;
  const dateRange = getDateRange(period, fromDate, toDate);

  if (dateRange?.startDate && dateRange?.endDate) {
    return {
      startDate: dateRange.startDate.split("T")[0],
      endDate: dateRange.endDate.split("T")[0],
    };
  }
  return {
    startDate: navDates[0],
    endDate: navDates[navDates.length - 1],
  };
}

function normalizeSignalSets(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return Object.values(raw);
  return [];
}

function timestampToYmdInTimeZone(timestamp, timeZone) {
  const numericTs = Number(timestamp);
  if (!Number.isFinite(numericTs)) return null;
  const d = new Date(numericTs);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return y && m && day ? `${y}-${m}-${day}` : null;
  } catch {
    return null;
  }
}

/**
 * Calendar YYYY-MM-DD for a signal instant in US Eastern (NYSE day).
 * Pure UTC was shifting US Sunday evening into Monday UTC → too few "non-trading" signals.
 */
function signalTimestampToYyyyMmDd(timestamp) {
  return timestampToYmdInTimeZone(timestamp, NYSE_SIGNAL_CALENDAR_TZ);
}

function ymdFromLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Count NYSE trading days (weekends + holidays excluded) from startYmd through endYmd inclusive. */
function countNyseTradingDaysInclusive(startYmd, endYmd) {
  if (!startYmd || !endYmd || startYmd > endYmd) return 0;
  let count = 0;
  const cur = parseIsoToLocalDate(startYmd);
  const end = parseIsoToLocalDate(endYmd);
  while (cur.getTime() <= end.getTime()) {
    const ymd = ymdFromLocalDate(cur);
    if (isTradingDayNY(ymd)) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * Signal-set frequency for the period:
 * - tradingDaysInPeriod = NYSE trading days in [startDate, endDate]
 * - totalSignalsInPeriod = all signal sets in that range (NYSE / US Eastern calendar day from timestamp)
 * - signalsOnTradingDays / signalsOnNonTradingDays = split by NYSE calendar
 * - signalCalendarYmds = unique US Eastern YYYY-MM-DD with ≥1 signal (any day), sorted

* Display average = round(tradingDaysInPeriod / totalSignalsInPeriod) when totalSignalsInPeriod > 0.
 */
function getSignalSetFrequencyMetrics(signalSets, startDate, endDate) {
  if (!startDate || !endDate) return null;
  const tradingDaysInPeriod = countNyseTradingDaysInclusive(startDate, endDate);
  const arr = normalizeSignalSets(signalSets);
  let totalSignalsInPeriod = 0;
  let signalsOnTradingDays = 0;
  const signalCalendarYmdSet = new Set();

  const signalsByYmd = {};
  for (const s of arr) {
    const ts = s?.date ?? s?.timestamp ?? s?.signal_date;
    const ymd = signalTimestampToYyyyMmDd(ts);
    if (!ymd || ymd < startDate || ymd > endDate) continue;
    totalSignalsInPeriod += 1;
    signalCalendarYmdSet.add(ymd);
    if (!signalsByYmd[ymd]) signalsByYmd[ymd] = [];
    signalsByYmd[ymd].push(s);
    if (isTradingDayNY(ymd)) {
      signalsOnTradingDays += 1;
    }
  }
  const signalsOnNonTradingDays = totalSignalsInPeriod - signalsOnTradingDays;
  const averageRounded =
    totalSignalsInPeriod > 0
      ? Math.round(tradingDaysInPeriod / totalSignalsInPeriod)
      : null;
  return {
    tradingDaysInPeriod,
    totalSignalsInPeriod,
    signalsOnTradingDays,
    signalsOnNonTradingDays,
    averageRounded,
    signalCalendarYmds: Array.from(signalCalendarYmdSet).sort(),
    signalsByYmd,
  };
}

function parseIsoDayUTC(date) {
  // Use UTC midnight for calendar-diff math
  return new Date(`${date}T00:00:00Z`);
}

function dayDiffCalendar(start, end) {
  const a = parseIsoDayUTC(start).getTime();
  const b = parseIsoDayUTC(end).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

function getReturnsInRangeWithDates(
  dailyReturns,
  startDate,
  endDate,
  ignoreNonTradingDays
) {
  if (startDate > endDate) return [];

  const dates = Array.from(dailyReturns.keys())
    .filter((d) => d >= startDate && d <= endDate)
    .sort((a, b) => a.localeCompare(b));

  const out = [];

  for (const d of dates) {
    if (ignoreNonTradingDays && !isTradingDayNY(d)) continue;

    const r = dailyReturns.get(d);
    if (r === undefined) continue;
    if (!Number.isFinite(r)) continue;

    out.push({ date: d, r });
  }

  return out;
}

/**
 * Sharpe ratio (annualized) from DAILY returns in [startDate, endDate].
 * Sharpe = sqrt(252) * mean(excess_daily) / std(excess_daily)
 */
function sharpeRatio(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    ignoreNonTradingDays = true,
  } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  const rets = series.map((x) => x.r);
  if (rets.length < (ddof === 1 ? 2 : 1)) return NaN;

  const rfDaily = riskFreeRateAnnual / tradingDays;
  const excess = rets.map((r) => r - rfDaily);

  const mu = mean(excess);
  const sigma = std(excess, ddof);
  if (!Number.isFinite(sigma) || sigma === 0) return NaN;

  return (mu / sigma) * Math.sqrt(tradingDays);
}

/**
 * Sortino ratio (annualized) from DAILY returns in [startDate, endDate].
 * Sortino = sqrt(252) * mean(excess_daily) / downsideDeviation
 *
 * downsideDeviation is std of negative (excess - threshold).
 */
function sortinoRatio(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    sortinoThreshold = 0,
    ignoreNonTradingDays = true,
  } = opts;

  const series = getReturnsInRangeWithDates(
    dailyReturns,
    startDate,
    endDate,
    ignoreNonTradingDays
  );
  const rets = series.map((x) => x.r);
  if (rets.length === 0) return NaN;

  const rfDaily = riskFreeRateAnnual / tradingDays;
  const excess = rets.map((r) => r - rfDaily);

  const downside = excess
    .map((x) => x - sortinoThreshold)
    .filter((x) => x < 0);

  if (downside.length < (ddof === 1 ? 2 : 1)) return NaN;

  const mu = mean(excess);
  const downsideSigma = std(downside, ddof);
  if (!Number.isFinite(downsideSigma) || downsideSigma === 0) return NaN;

  return (mu / downsideSigma) * Math.sqrt(tradingDays);
}

/**
 * Calculate return from NAV series for a given period.
 * @param {Map|Object} navSeries - NAV series with date keys (YYYY-MM-DD)
 * @param {string} startDate - period start date
 * @param {string} endDate - period end date
 * @param {Object} options
 * @param {"chart"|"calendar"} [options.mode]
 */
function periodReturnFromNav(navSeries, startDate, endDate, options = {}) {
  const { mode = "chart" } = options;

  const entries = navSeries instanceof Map
    ? Array.from(navSeries.entries())
    : Object.entries(navSeries);

  if (entries.length === 0) return NaN;

  const sortedDates = entries.map(([date]) => date).sort();
  let startNavDate, endNavDate;

  if (mode === "calendar") {
    // Start: last NAV ≤ startDate (so we include first day's return)
    for (let i = sortedDates.length - 1; i >= 0; i--) {
      if (sortedDates[i] <= startDate) {
        startNavDate = sortedDates[i];
        break;
      }
    }

    // End: last NAV ≤ endDate
    for (let i = sortedDates.length - 1; i >= 0; i--) {
      if (sortedDates[i] <= endDate) {
        endNavDate = sortedDates[i];
        break;
      }
    }
  } else {
    // Chart / continuous TWR: backward-fill
    for (let i = sortedDates.length - 1; i >= 0; i--) {
      if (sortedDates[i] <= startDate) {
        startNavDate = sortedDates[i];
        break;
      }
    }
    for (let i = sortedDates.length - 1; i >= 0; i--) {
      if (sortedDates[i] <= endDate) {
        endNavDate = sortedDates[i];
        break;
      }
    }
  }

  if (!startNavDate || !endNavDate) return NaN;

  const getNav = d => navSeries instanceof Map ? navSeries.get(d) : navSeries[d];
  const startNav = getNav(startNavDate);
  const endNav = getNav(endNavDate);

  if (startNav == null || endNav == null || startNav === 0) return NaN;

  return endNav / startNav - 1;
}
function navToDailyReturns(navByDate, opts = {}) {
  const { ignoreNonTradingDays = true } = opts;

  const dates = Array.from(navByDate.keys()).sort((a, b) =>
    a.localeCompare(b)
  );
  const out = new Map();

  let prevNav = null;
  let prevDate = null;

  for (const d of dates) {
    if (ignoreNonTradingDays && !isTradingDayNY(d)) continue;

    const nav = navByDate.get(d);
    if (nav === undefined || !Number.isFinite(nav) || nav <= 0) continue;

    // First valid day has no prior day return; skip it (same as most libs)
    if (prevNav !== null && prevDate !== null) {
      const r = nav / prevNav - 1;
      if (Number.isFinite(r)) out.set(d, r);
    }

    prevNav = nav;
    prevDate = d;
  }

  return out;
}

function backtestSummaryFromNav(navSeries, startDate, endDate, opts = {}) {
  const dailyReturns = navToDailyReturns(navSeries, {
    ignoreNonTradingDays: opts.ignoreNonTradingDays ?? true,
  });

  // Determine first NAV for TWR calculation
  const navDates = Array.from(navSeries.keys())
    .filter(d => d >= startDate && d <= endDate)
    .sort((a, b) => a.localeCompare(b));
  const firstNavDate = navDates.length > 0 ? navDates[0] : startDate;

  // Pass mode to periodReturnFromNav
  const mode = opts.mode || "chart"; // default chart for continuous

  const totalTwr = periodReturnFromNav(navSeries, startDate, endDate, { mode });

  return backtestSummary(dailyReturns, startDate, endDate, {
    ...opts,
    firstNavDate,
    navSeries,
    totalTwr, // use precomputed TWR
  });
}
/**
 * Full backtest summary matching the fields in your sample output.
 * Percent fields are returned as percent numbers (e.g. -0.76 means -0.76%).
 *
 * By default, filters out weekends/NYSE holidays for Sharpe/Sortino/Vol/AvgDD/Drawdowns.
 * TotalTWR/Annualized also uses the filtered series (which is standard).
 */

function backtestSummary(dailyReturns, startDate, endDate, opts = {}) {
  const {
    tradingDays = 252,
    riskFreeRateAnnual = 0,
    ddof = 1,
    sortinoThreshold = 0,
    ignoreNonTradingDays = true,
    firstNavDate, // First NAV date from original series
    navSeries,    // Original NAV series for TWR calculation
    mode = "chart", // "chart" (continuous) or "calendar" (table / period returns)
  } = opts;

  // Get daily returns within period
  const series = getReturnsInRangeWithDates(dailyReturns, startDate, endDate, ignoreNonTradingDays);
  const n = series.length;
  const firstReturnDate = n ? series[0].date : null;

  // --- Determine effective start date based on mode ---
  let effectiveStartDate;
  if (mode === "calendar" && navSeries) {
    // First NAV >= startDate
    const sortedNavDates = Array.from(navSeries.keys()).sort();
    effectiveStartDate = sortedNavDates.find(d => d >= startDate) || startDate;
  } else {
    // Chart / continuous TWR: previous NAV <= startDate
    effectiveStartDate = firstNavDate ? firstNavDate : (firstReturnDate ?? startDate);
  }

  const effectiveEndDate = n ? series[n - 1].date : endDate;

  // --- Equity curve + drawdowns ---
  let equity = 1.0;
  let currentPeakValue = 1.0;
  let currentPeakDate = effectiveStartDate;

  let worstDD = 0.0;
  let worstPeakDate = currentPeakDate;
  let worstPeakValue = 1.0;
  let worstTroughDate = currentPeakDate;
  let worstTroughValue = 1.0;

  const drawdowns = [];
  const equityPath = [];

  for (const { date, r } of series) {
    equity *= 1 + r;

    if (equity >= currentPeakValue) {
      currentPeakValue = equity;
      currentPeakDate = date;
    }

    const dd = currentPeakValue === 0 ? NaN : equity / currentPeakValue - 1;
    if (Number.isFinite(dd)) drawdowns.push(dd);

    if (dd < worstDD) {
      worstDD = dd;
      worstPeakDate = currentPeakDate;
      worstPeakValue = currentPeakValue;
      worstTroughDate = date;
      worstTroughValue = equity;
    }

    equityPath.push({ date, equity });
  }

  // --- Calculate TWR ---
  let totalTwr;
  if (navSeries && startDate && endDate) {
    totalTwr = periodReturnFromNav(navSeries, startDate, endDate, { mode });
    if (isNaN(totalTwr) && n > 0) totalTwr = equity - 1;
  } else {
    totalTwr = n > 0 ? equity - 1 : NaN;
  }

  // --- Annualized return based on calendar period ---
  let annualized = NaN;
  if (effectiveStartDate && effectiveEndDate && Number.isFinite(totalTwr)) {
    const start = new Date(effectiveStartDate);
    const end = new Date(effectiveEndDate);
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const years = diffDays / 365.25;

    if (years > 0) {
      annualized = Math.pow(1 + totalTwr, 1 / years) - 1;
    }
  }

  if (isNaN(annualized) && n > 0 && Number.isFinite(totalTwr)) {
    annualized = Math.pow(1 + totalTwr, tradingDays / n) - 1;
  }

  const rets = series.map(x => x.r);
  const vol = rets.length >= (ddof === 1 ? 2 : 1) ? std(rets, ddof) * Math.sqrt(tradingDays) : NaN;
  const avgDD = drawdowns.length ? mean(drawdowns) : NaN;

  // --- Recovery / worst drawdown ---
  let recoveryDate = effectiveEndDate ?? null;
  let daysToRecover = null;

  if (n > 0 && worstDD < 0) {
    const troughIdx = equityPath.findIndex(x => x.date === worstTroughDate);
    if (troughIdx >= 0) {
      const after = equityPath.slice(troughIdx + 1);
      const recovered = after.find(x => x.equity >= worstPeakValue);
      if (recovered) {
        recoveryDate = recovered.date;
        daysToRecover = dayDiffCalendar(worstTroughDate, recoveryDate);
      } else {
        recoveryDate = equityPath[equityPath.length - 1].date;
        daysToRecover = null;
      }
    }
  }

  const daysFromStartToEnd = worstPeakDate && recoveryDate
    ? dayDiffCalendar(worstPeakDate, recoveryDate)
    : null;

  return {
    backtest: {
      startDate,
      endDate,
      n,
      effectiveStartDate,
      effectiveEndDate,
    },

    totalTimeWeightedReturnPct: totalTwr * 100,
    annualizedReturnPct: annualized * 100,

    sharpe: sharpeRatio(dailyReturns, startDate, endDate, {
      tradingDays,
      riskFreeRateAnnual,
      ddof,
      sortinoThreshold,
      ignoreNonTradingDays,
    }),

    sortino: sortinoRatio(dailyReturns, startDate, endDate, {
      tradingDays,
      riskFreeRateAnnual,
      ddof,
      sortinoThreshold,
      ignoreNonTradingDays,
    }),

    volatilityPct: vol * 100,
    averageDrawdownPct: avgDD * 100,

    worstDrawdown: {
      startDate: n ? worstPeakDate : null,
      endDate: n ? recoveryDate : null,
      troughDate: n ? worstTroughDate : null,
      daysToRecover: n ? daysToRecover : null,
      daysFromStartToEnd: n ? daysFromStartToEnd : null,
      startValue: n ? worstPeakValue : null,
      troughValue: n ? worstTroughValue : null,
      drawdownPct: worstDD * 100,
    },
  };
}

export {
  backtestSummaryFromNav,
  resolveNavPeriodBounds,
  getSignalSetFrequencyMetrics,
};
